// SystemUpdateCenter — "تحديث النظام" من لوحة التحكّم (3.4.0، المرحلة 2 من المُرقّي).
// يعرض الإصدار الحالي مقابل المعلّق، ويطبّق ترحيلات المخطّط **على مراحل** بزرّ واحد
// (3.4.0→3.7.0 يمشي عبر كل خطوة بنفسه). التطبيق/التأسيس لمدير النظام فقط (canApply).
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

export default function SystemUpdateCenter() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { ok, steps, count, message }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/upgrade/status');
      setStatus(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'تعذّر قراءة حالة الترقية');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runApply = useCallback(async (targetVersion) => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await api.post('/admin/upgrade/apply', targetVersion ? { targetVersion } : {});
      setResult(res.data);
      await load();
    } catch (e) {
      setResult(e?.response?.data || { ok: false, message: e.message });
    } finally {
      setBusy(false);
    }
  }, [load]);

  const runBaseline = useCallback(async () => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await api.post('/admin/upgrade/baseline');
      setResult({ ok: true, message: t('baseline_done', { count: res.data.baselined }) });
      await load();
    } catch (e) {
      setResult(e?.response?.data || { ok: false, message: e.message });
    } finally {
      setBusy(false);
    }
  }, [load, t]);

  if (loading) {
    return (
      <SmartPage>
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
          <CircularProgress />
        </Box>
      </SmartPage>
    );
  }

  const pendingVersions = status?.pendingVersions || [];
  const hasPending = (status?.pending?.length || 0) > 0;
  const canApply = status?.canApply === true;

  return (
    <SmartPage>
      <SmartHeader
        title={t('system_update', 'تحديث النظام')}
        subtitle={t('system_update_hint', 'ترقية قاعدة البيانات على مراحل من داخل البرنامج — دون رفع يدوي.')}
        actions={(
          <Button onClick={load} startIcon={<RefreshIcon />} variant="outlined"
            sx={{ borderColor: C.line, color: C.text, fontWeight: 900 }}>
            {t('refresh', 'تحديث')}
          </Button>
        )}
      />

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {!canApply ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('upgrade_admin_only', 'التطبيق متاح لمدير النظام فقط — يمكنك عرض الحالة هنا.')}
        </Alert>
      ) : null}

      {/* بطاقة الإصدارات */}
      <Card variant="outlined" sx={{ borderColor: C.line, bgcolor: C.card, mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} divider={<Divider orientation="vertical" flexItem />}>
            <Box>
              <Typography variant="caption" sx={{ color: C.muted, fontWeight: 800 }}>{t('app_version', 'إصدار التطبيق')}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: C.text }}>v{status?.appVersion || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: C.muted, fontWeight: 800 }}>{t('schema_version', 'إصدار المخطّط')}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: C.text }}>
                {status?.schemaVersion ? `v${status.schemaVersion}` : t('baseline_label', 'أساس')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: C.muted, fontWeight: 800 }}>{t('applied_migrations', 'مطبّق/الكلّ')}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: C.text }}>{status?.appliedCount ?? 0}/{status?.total ?? 0}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* تركيب قائم غير مؤسَّس بعد */}
      {!status?.initialized ? (
        <Card variant="outlined" sx={{ borderColor: C.warning, bgcolor: C.warningSoft, mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontWeight: 900, color: C.text, mb: 0.5 }}>{t('not_initialized', 'لم يُؤسَّس تتبّع الترحيلات بعد')}</Typography>
            <Typography variant="body2" sx={{ color: C.text, mb: 1.5 }}>
              {t('baseline_explain', 'هذا تركيب قائم. التأسيس يُعلّم الترحيلات الحالية كمطبّقة دون إعادة تشغيلها، ثم تظهر الترقيات الجديدة هنا.')}
            </Typography>
            <Button disabled={!canApply || busy} onClick={runBaseline} variant="contained"
              sx={{ bgcolor: C.gold, color: '#fff', fontWeight: 900, '&:hover': { bgcolor: C.goldHover } }}>
              {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : t('initialize_baseline', 'تأسيس (baseline)')}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* الترقيات المعلّقة */}
      <Card variant="outlined" sx={{ borderColor: C.line, bgcolor: C.card }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 900, color: C.text }}>{t('pending_upgrades', 'الترقيات المعلّقة')}</Typography>
            {hasPending ? (
              <Chip label={`${status.pending.length}`} size="small" sx={{ bgcolor: C.goldSoft, color: C.text, fontWeight: 800 }} />
            ) : (
              <Chip icon={<CheckCircleIcon />} label={t('up_to_date', 'محدّث')} size="small" sx={{ bgcolor: C.greenSoft, color: C.green, fontWeight: 800 }} />
            )}
          </Stack>

          {hasPending ? (
            <>
              {pendingVersions.length ? (
                <Typography variant="body2" sx={{ color: C.muted, mb: 1 }}>
                  {t('staged_through', 'سيُطبَّق على مراحل عبر الإصدارات:')} {pendingVersions.map((v) => `v${v}`).join(' → ')}
                </Typography>
              ) : null}
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {status.pending.map((p) => (
                  <Stack key={p.file} direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={p.version ? `v${p.version}` : 'legacy'} sx={{ bgcolor: C.cardSoft, color: C.text, fontWeight: 700, minWidth: 64 }} />
                    <Typography variant="body2" sx={{ color: C.text, fontFamily: 'monospace' }}>{p.file}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button disabled={!canApply || busy} onClick={() => runApply(null)} variant="contained" startIcon={busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
                  sx={{ bgcolor: C.appbar, color: '#fff', fontWeight: 900, '&:hover': { bgcolor: C.appbarHover } }}>
                  {t('apply_all_staged', 'تطبيق الكل على مراحل')}
                </Button>
                {pendingVersions.length > 1 ? (
                  <Button disabled={!canApply || busy} onClick={() => runApply(pendingVersions[0])} variant="outlined"
                    sx={{ borderColor: C.line, color: C.text, fontWeight: 900 }}>
                    {t('apply_next_only', 'الخطوة التالية فقط')} (v{pendingVersions[0]})
                  </Button>
                ) : null}
              </Stack>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: C.muted }}>{t('no_pending', 'لا ترقيات معلّقة — النظام محدّث.')}</Typography>
          )}
        </CardContent>
      </Card>

      {/* نتيجة آخر تطبيق */}
      {result ? (
        <Card variant="outlined" sx={{ mt: 2, borderColor: result.ok ? C.green : C.rose, bgcolor: C.card }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              {result.ok ? <CheckCircleIcon sx={{ color: C.green }} /> : <ErrorOutlineIcon sx={{ color: C.rose }} />}
              <Typography sx={{ fontWeight: 900, color: C.text }}>
                {result.ok ? t('upgrade_done', 'تمّت الترقية') : t('upgrade_failed', 'فشلت الترقية')}
              </Typography>
            </Stack>
            {result.message ? <Typography variant="body2" sx={{ color: C.text, mb: 1 }}>{result.message}</Typography> : null}
            {(result.steps || []).filter((s) => s.state === 'done' || s.state === 'error').map((s, i) => (
              <Stack key={`${s.file}-${i}`} direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
                {s.state === 'error' ? <ErrorOutlineIcon sx={{ color: C.rose, fontSize: 18 }} /> : <CheckCircleIcon sx={{ color: C.green, fontSize: 18 }} />}
                <Typography variant="body2" sx={{ color: C.text, fontFamily: 'monospace' }}>
                  {s.file}{s.error ? ` — ${s.error}` : ''}
                </Typography>
              </Stack>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </SmartPage>
  );
}
